<template>
  <div class="lines-container">

    <!-- ========== ENCABEZADO ========== -->
    <section class="chat-header">
      <h1 class="chat-header__title">
        Administrar Chatbots
      </h1>
      <p class="chat-header__subtitle">Activa o configura los chatbots por línea</p>
    </section>

    <!-- ========== GRID DE TARJETAS O SPINNER DE CARGA ========== -->
    <div class="widgets-grid">
      <!-- Spinner mientras se cargan las líneas -->
      <div v-if="loading" class="loading-spinner">
        <span class="spinner"></span>
        <p>Cargando Líneas...</p>
      </div>

      <!-- Tarjetas de líneas disponibles -->
      <div
        v-else
        v-for="line in lines"
        :key="line.id"
        class="widget-card"
        :class="{ 'dimmed': hoveredWidget !== null && hoveredWidget !== line.id }"
        @mouseenter="hoveredWidget = line.id"
        @mouseleave="hoveredWidget = null"
      >
        <!-- Parte superior de la tarjeta (icono + info) -->
        <div class="widget-card__top">
          <ion-icon name="hardware-chip-outline" class="widget-card__icon" />
          <div class="widget-card__info">
            <h3 class="widget-card__title">{{ line.line_name }}</h3>
            <p class="widget-card__subtitle">{{ line.phone_number }}</p>
          </div>
        </div>

        <!-- Botón para configurar el chatbot de la línea -->
        <button class="widget-card__button" @click="goToBuilder(line.id)">
          <ion-icon name="construct-outline" />
          <span>Configurar</span>
        </button>

        <!-- Interruptor para activar o desactivar el chatbot -->
        <div class="switch-container">
          <span>Chatbot</span>
          <label class="switch">
            <input type="checkbox" :checked="line.has_chatbot" @change="toggleChatbot(line)">
            <span class="slider round"></span>
          </label>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "ChatbotLinesView",
  data() {
    return {
      lines: [],               // Arreglo de líneas disponibles
      hoveredWidget: null,     // ID del widget actualmente enfocado por el mouse
      loading: true,           // Estado de carga de las líneas
    };
  },
  methods: {
    // ========== CARGA DE LÍNEAS DESDE EL SERVIDOR ==========
    async fetchLines() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          console.error("Token no disponible");
          return;
        }

        const response = await axios.get("/lines", {
          headers: { Authorization: `Bearer ${token}` },
        });

        this.lines = response.data; 
      } catch (error) {
        console.error("Error al obtener las líneas:", error);
      } finally {
        this.loading = false; 
      }
    },

    // ========== ACTIVAR/DESACTIVAR EL CHATBOT EN UNA LÍNEA ==========
    async toggleChatbot(line) {
      try {
        const token = localStorage.getItem("token");
        await axios.put(`/lines/${line.id}/toggle-chatbot`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });

        line.has_chatbot = !line.has_chatbot;
      } catch (error) {
        console.error("Error al cambiar el estado del chatbot:", error);
      }
    },

    // ========== NAVEGACIÓN AL CONSTRUCTOR DE CHATBOTS ==========
    goToBuilder(lineId) {
      this.$router.push({ name: "ChatbotBuilder", params: { lineId } });
    }
  },

  mounted() {
    // Ejecutar carga de líneas al montar el componente
    this.fetchLines(); 
  },
};
</script>

<style scoped>

/* ========== CONTENEDOR PRINCIPAL ========== */
.lines-container {
  padding: 20px;
  background: #f9fafb;
  height: 100vh;
  overflow-y: auto; 
}

/* ========== ENCABEZADO ========== */
.chat-header {
  margin: 0;
  text-align: left;
  font-family: 'Manrope', sans-serif;
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
}

.chat-header__title {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-header__subtitle {
  font-size: 1rem;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

/* ========== GRID DE TARJETAS ========== */
.widgets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 30px;
  margin-bottom: 60px;
  font-family: 'Manrope', sans-serif;
}

/* ========== TARJETA DE LÍNEA ========== */
.widget-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  cursor: pointer;
}

.widget-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

/* ========== ENCABEZADO DE LA TARJETA ========== */
.widget-card__top {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.widget-card__icon {
  font-size: 32px;
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  padding: 10px;
  border-radius: 50%;
}

.widget-card__info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.widget-card__title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.widget-card__subtitle {
  font-size: 0.88rem;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 2px;
}

/* ========== BOTÓN DE CONFIGURACIÓN ========== */
.widget-card__button {
  margin-top: 20px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.25s ease, transform 0.15s ease;
  font-family: 'Manrope', sans-serif;
}

.widget-card__button:hover {
  background: linear-gradient(135deg, #2563eb, #1e40af);
}

.widget-card__button:active {
  background: linear-gradient(135deg, #1e40af, #1e3a8a);
  transform: scale(0.97);
}

.widget-card__button ion-icon {
  font-size: 18px;
}

/* ========== INTERRUPTOR DE CHATBOT ========== */
.switch-container {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: bold;
  color: #1f2937;
  margin-top: 15px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #d1d5db;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #4ade80;
}

input:checked + .slider:before {
  transform: translateX(18px);
}

/* ========== EFECTO DE ATENUACIÓN EN TARJETAS NO HOVER ========== */
.dimmed {
  opacity: 0.88;
  filter: brightness(0.97) blur(0.4px);
  transition: all 0.3s ease;
}

/* ========== SPINNER DE CARGA ========== */
.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  grid-column: 1 / -1;
  padding: 60px 20px;
  font-size: 16px;
  color: #64748b;
  gap: 16px;
  font-family: 'Manrope', sans-serif;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 5px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s ease-in-out infinite;
  position: relative;
}

.spinner::after {
  content: '';
  position: absolute;
  top: -8px;
  left: -8px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.1);
  animation: pulse 1.6s infinite ease-in-out;
}

/* ========== ANIMACIONES ========== */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.2;
  }
}

/* ========== RESPONSIVE (MÓVILES) ========== */
@media (max-width: 768px) {
  .widgets-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  .widget-card {
    padding: 20px 15px;
  }

  .widget-card__icon {
    font-size: 50px;
    padding: 12px;
  }

  .widget-card__title {
    font-size: 18px;
  }

  .widget-card__button {
    padding: 10px 20px;
    font-size: 14px;
  }
}
</style>